<script setup lang="ts">
  import { computed } from 'vue'
  import {
    Alert,
    AlertDescription,
    AlertTitle,
    Button,
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
    FlagIcon,
    Kbd
  } from '@gat-ship/vue-components'
  import {
    IconCheck,
    IconQuestion,
    IconSearch,
    IconVessel
  } from '@gat-ship/icons'
  import type { StepOption } from './create-portcall-steps'
  import type { StepListSource } from '../../../../composables/usePortcallStepSources'
  import { VESSEL_TBN } from '../../../../composables/usePortcallApiCalls'
  import { usePortcallStepListbox } from '../../../../composables/usePortcallStepListbox'
  import PortcallStepSearch from './PortcallStepSearch.vue'
  import {
    ModalSectionHeader,
    ModalSectionHeaderTitle
  } from '@gat-ship/vue-components'
  import PortcallStepLoading from './PortcallStepLoading.vue'
  import PortcallStepEmpty from './PortcallStepEmpty.vue'

  /**
   * The vessel step (What). Server search keyed by IMO; rows show the vessel's
   * flag state when known. Owns the "to be named" affordance entirely — the
   * button lives in the empty state and selects the {@link VESSEL_TBN}
   * sentinel like any other option.
   */
  const props = defineProps<{
    source: StepListSource
    selectedValue?: string
    /** Mobile keyboard is up — collapse the question block to free list room. */
    keyboardOpen?: boolean
  }>()
  const emit = defineEmits<{
    select: [option: StepOption, advance: boolean]
    'update:query': [value: string]
  }>()

  const {
    onKeyboardAdvance,
    onPointerSelect,
    onSelect,
    selectImmediate,
    awaitingFirstSearch
  } = usePortcallStepListbox(
    props.source,
    (option, advance) => emit('select', option, advance),
    { searchFirst: true, suppressEnterSelect: () => !!props.keyboardOpen }
  )

  /** Whether the step currently holds the "to be named" sentinel. */
  const isTbn = computed(() => props.selectedValue === VESSEL_TBN)

  // Clear the vessel selection and the search query so the step drops back to a
  // fresh search; the empty state then re-offers the "to be named" affordance.
  function resetVessel() {
    emit('select', { value: '', label: '' }, false)
    emit('update:query', '')
  }
</script>

<template>
  <Command
    ref="commandRef"
    class="flex h-auto min-h-0 flex-1 flex-col overflow-hidden bg-transparent"
    @keydown.enter.capture="onKeyboardAdvance"
  >
    <div :class="['flex-none px-6 pb-3', keyboardOpen ? 'pt-4' : 'pt-6']">
      <ModalSectionHeader v-if="!keyboardOpen" class="gap-0">
        <div
          class="text-muted-foreground mb-2 text-[11px] font-semibold uppercase tracking-wider"
        >
          Vessel
        </div>
        <ModalSectionHeaderTitle class="text-lg font-semibold leading-tight">
          Which vessel is calling?
        </ModalSectionHeaderTitle>
      </ModalSectionHeader>
      <div v-if="isTbn" class="mt-4">
        <Alert variant="info">
          <IconQuestion />
          <AlertTitle>Vessel set to "To be named" (TBN)</AlertTitle>
          <AlertDescription>
            No specific vessel is assigned yet — you can name it later, or reset
            to search for the actual vessel.
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="sm"
          class="mt-3 w-fit"
          @click="resetVessel"
        >
          <IconSearch class="size-4" />
          Reset &amp; search for a vessel
        </Button>
      </div>

      <PortcallStepSearch
        v-else
        class="mt-4"
        :model-value="source.query.value"
        :keyboard-open="keyboardOpen"
        placeholder="Search by name or IMO…"
        @update:model-value="emit('update:query', $event)"
      />
    </div>

    <CommandList v-if="!isTbn" class="max-h-none min-h-0 flex-1 px-6 pb-4">
      <PortcallStepLoading v-if="source.loading.value" />

      <CommandGroup
        v-else-if="source.options.value.length"
        class="divide-border divide-y p-0"
      >
        <CommandItem
          v-for="opt in source.options.value"
          :key="opt.value"
          :value="opt.value"
          class="group gap-3 rounded-none px-2 py-3"
          @select="onSelect(opt)"
          @dblclick="selectImmediate(opt)"
          @pointerdown="onPointerSelect"
        >
          <FlagIcon
            v-if="opt.flag"
            :flag="opt.flag"
            class="size-4 shrink-0 rounded-sm"
          />
          <IconVessel v-else class="text-muted-foreground size-4 shrink-0" />
          <div class="min-w-0 flex-1">
            <div
              class="truncate text-sm font-medium"
              :class="selectedValue === opt.value && 'text-primary'"
            >
              {{ opt.label }}
            </div>
            <div v-if="opt.sub" class="text-muted-foreground truncate text-xs">
              {{ opt.sub }}
            </div>
          </div>
          <div class="ml-auto flex items-center gap-2">
            <Kbd class="hidden md:group-data-[highlighted]:inline-flex">↵</Kbd>
            <IconCheck
              v-if="selectedValue === opt.value"
              class="text-primary size-4 shrink-0"
            />
          </div>
        </CommandItem>
      </CommandGroup>

      <PortcallStepEmpty
        v-else
        :awaiting-first-search="awaitingFirstSearch"
        prompt="Start typing to search vessels by name or IMO — or use Skip (TBN) below if the vessel isn't known yet."
      />
    </CommandList>
  </Command>
</template>
